/**
 * Rows grouped by what the reader owes, not by stage: Needs you, Ready to
 * merge, Working, Stopped, Done. The group's class carries its colour (blue
 * only for the human lane, which Ready to merge is part of, green only while
 * an agent is moving, amber for the product
 * telling on itself). A group with no rows prints nothing unless it has an
 * `empty` sentence, which prints as a dashed line.
 */
import type { CSSProperties, ReactNode } from "react";
import { Children } from "react";
import { cx } from "./primitives.js";

export type GroupTone = "needs" | "ready" | "live" | "stop" | "done";

export interface GroupProps {
  label: ReactNode;
  tone?: GroupTone;
  /** The count in the header; defaults to the number of rows passed. */
  count?: number;
  /** A Team's colour dot before the label (Waiting on you groups by Team). */
  dot?: string;
  /** Print the label in plain ink whatever the tone. */
  plain?: boolean;
  /** Printed dashed when the group has no rows; without it the group is omitted. */
  empty?: ReactNode;
  /** At the right of the header, after the rule (a pager). */
  aside?: ReactNode;
  /** Under the rows (a second pager at the end of a long list). */
  foot?: ReactNode;
  children?: ReactNode;
}

export function Group({
  label,
  tone,
  count,
  dot,
  plain,
  empty,
  aside,
  foot,
  children,
}: GroupProps) {
  const rows = Children.toArray(children);
  if (rows.length === 0 && empty == null) return null;
  return (
    <div className={cx("group", tone)}>
      <div className="gh">
        {dot && <span className="tdot" style={{ "--tc": dot } as CSSProperties} aria-hidden />}
        <span className={cx("t", plain && "plain")}>{label}</span>
        <span className="n">{count ?? rows.length}</span>
        <span className="rule" aria-hidden />
        {aside}
      </div>
      {rows.length > 0 ? (
        <div className="rows">{rows}</div>
      ) : (
        <div className="empty dashed tight">
          <p>{empty}</p>
        </div>
      )}
      {foot}
    </div>
  );
}

/** The scrolling list under a ListHead. */
export function Groups({ children }: { children: ReactNode }) {
  return <div className="qlist">{children}</div>;
}
